import React, { useCallback, memo, useRef, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { FaUser, FaCalendar, FaMapMarkerAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import dayjs from 'dayjs';
import NumberFormat from 'react-number-format';
import Tooltip from '@material-ui/core/Tooltip';
import { useRouter } from 'next/router';
import { makeStyles } from '@material-ui/core';
import {
  CabinTypeLabel,
  CabinType,
  // DiscountEligibility,
  PricePerPersonDisclaimerTooltipText,
  EnumMergedItineraryState,
  TravelTypes,
} from '../../../constants';
import CallForPricing from '../CallForPricing';
import PortsOfCall from '../PortsOfCall';
import Radio from '../../Radio';
import PriceBreakdown from '../PriceBreakdown';
import InfoDropdownPopover from '../../InfoDropdownPopover';
import { priceRequest } from '../../../store/slices/cruise';

import AMEXBenefitsInfo from './AMEXBenefitsInfo';
import PromotionInfo from '../PromotionInfo';
import VirtuosoPromotions from '../VirtuosoPromotions';
import CostFromPrice from './CostFromPrice';

import AdditionalInfoModal from '../AdditionalInfoModal';
import formatPriceRequest from '../../../helpers/formatPriceRequest';
import formatStateroomsInput from '../../../helpers/formatStateroomsInput';
import AgentTipsInline from './AgentTipsInline';

import Table from '../../Table';
import AvatarHeading from '../../AvatarHeading';
import Button from '../../Button';

import getLowestCostForRow from '../../../helpers/getLowestCostForRow';
import CruiseBenefits from './CruiseBenefits';

const mapCruiseInfoProps = (cruiseLine, ship) => {
  const arr = [];
  if (cruiseLine && cruiseLine.cruiseLineName)
    arr.push({ name: 'Cruise line', value: cruiseLine.cruiseLineName });
  if (ship && ship.shipName) arr.push({ name: 'Ship', value: ship.shipName });
  if (ship && ship.shipYearBuilt) arr.push({ name: 'Built', value: ship.shipYearBuilt });
  if (ship && ship.shipYearRefurbished)
    arr.push({ name: 'Refurbished', value: ship.shipYearRefurbished });
  if (ship && ship.shipCapacity) arr.push({ name: 'Capacity', value: ship.shipCapacity });
  return arr;
};

const useStyles = makeStyles(() => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
  },
}));

const CruiseInformation = ({
  isInsideModal,
  selectedItineraryRow,
  handleViewCabinsClick,
  handleItineraryRowClick,
  handleDoShowAgentTip,
  canShowAgentTips,
  cruiseItinerary,
  cruiseOption,
  doHideAllRows,
}) => {
  const classes = useStyles();
  const router = useRouter();
  const { debug } = router.query;
  const doShowDebugInfo = debug === 'true';
  const viewRulesEl = useRef(null);
  const { travelAgency } = useSelector((store) => store.user);
  const { staterooms } = useSelector((state) => state.tripSearch.searchObj);
  const { cruiseOptions } = useSelector((state) => state.compare);

  const travelOption =
    useSelector(
      (state) =>
        cruiseOption ||
        (state.tripSearch.searchResult.travelOptions || []).find((o) =>
          o.key.includes(selectedItineraryRow.itineraryId),
        ),
    ) ||
    (cruiseItinerary && cruiseItinerary.itineraryDetail) ||
    (cruiseOptions && _.find(cruiseOptions, (c) => c.key === selectedItineraryRow.itineraryId));
  const [costFrom, setCostFrom] = useState(getLowestCostForRow(selectedItineraryRow));
  const mappedCruiseProps = mapCruiseInfoProps(travelOption.cruiseLine, travelOption.ship);
  // const isAmexDiscountSelected = discounts.includes(DiscountEligibility.AMEX_MEMBER);
  const dispatch = useDispatch();
  const doShowAllRows = !doHideAllRows && !cruiseItinerary && travelOption.itineraryRows;
  const handlePriceBreakdownOpen = useCallback(() => {
    dispatch(
      priceRequest(
        formatPriceRequest({
          itineraryId: selectedItineraryRow.itineraryId,
          staterooms,
          cruiseLineId: travelOption.cruiseLine.cruiseLineId,
        }),
      ),
    );
  }, [travelOption, dispatch, selectedItineraryRow.itineraryId, staterooms]);

  const doShowStatusColumn = useMemo(
    () =>
      !!_.find(
        travelOption.itineraryRows,
        (r) => r.itineraryStatus || (doShowDebugInfo && r.itineraryState !== null),
      ),
    [doShowDebugInfo, travelOption.itineraryRows],
  );

  useEffect(() => {
    setCostFrom(getLowestCostForRow(selectedItineraryRow));
  }, [selectedItineraryRow]);

  return (
    <div>
      <div className="sm:py-4 sm:px-8 py-2 px-4">
        <div className="grid grid-cols-3">
          <AvatarHeading
            imageSrc={travelOption.cruiseLine.cruiseLineLogoThumb}
            imageTitle={travelOption.cruiseLine.cruiseLineName}
            title={travelOption.itineraryTitle}
            subTitle={travelOption.sailingDescription}
            doShowCruiseTourLabel={travelOption.itineraryCruiseTour}
            className="col-span-3 sm:col-span-2"
          >
            <>
              {mappedCruiseProps.map((v, i) => (
                <div className="sm:whitespace-nowrap" key={v.name}>
                  {`${v.name}: ${v.value} `}
                  {i < mappedCruiseProps.length - 1 && <span>&bull;&nbsp;</span>}
                </div>
              ))}
            </>
          </AvatarHeading>
          {doShowAllRows && (
            <div className="text-right">
              <div className="text-gray-600 text-xs xl:text-sm font-bold leading-none">
                {costFrom && costFrom.price ? `${CabinTypeLabel[costFrom.cabinType]} from` : ''}
              </div>
              <div className="text-sm md:text-2xl font-bold leading-none">
                {costFrom && costFrom.price ? (
                  <>
                    <Tooltip title={PricePerPersonDisclaimerTooltipText} arrow classes={classes}>
                      <span>
                        <NumberFormat
                          value={costFrom.price}
                          prefix="$"
                          displayType="text"
                          thousandSeparator
                          fixedDecimalScale="true"
                          decimalScale="2"
                        />
                        <span>/pp</span>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <CallForPricing />
                )}
              </div>
              <InfoDropdownPopover
                onOpen={handlePriceBreakdownOpen}
                dropdownTextPlaceholder="Get total price/breakdown"
              >
                <PriceBreakdown
                  payload={{
                    itineraryId: selectedItineraryRow.itineraryId,
                    cruiseLineId: travelOption.cruiseLine.cruiseLineId,
                    staterooms,
                    mode: TravelTypes.cruise,
                  }}
                />
              </InfoDropdownPopover>
            </div>
          )}
        </div>
        <div className={`mt-5 grid ${doShowAllRows ? 'grid-cols-3' : 'grid-cols-1'}`}>
          <div className="px-8 py-4 bg-gray-100 col-span-3 xl:col-span-1 border-2 xl:border-r-0 xs:border-b-0">
            <div className="font-bold">Cruise overview</div>
            <div className="text-sm mt-1">
              <FaUser className="inline-block mb-1 h-3 text-theme" />
              <div className="inline-block ml-1">
                {formatStateroomsInput(
                  staterooms,
                  travelAgency && travelAgency.enabledTransportModes,
                )}
              </div>
            </div>
            <div className="text-sm mt-1">
              <FaCalendar className="inline-block mb-1 h-3 text-theme" />
              <div className="inline-block ml-1">
                {`${dayjs(selectedItineraryRow.itineraryDepartDate).format('MMM D, YYYY')} - ${dayjs(selectedItineraryRow.itineraryReturnDate).format('MMM D, YYYY')} 
                (${selectedItineraryRow.itineraryNumNights} nights) `}
              </div>
            </div>
            <div className="text-sm mt-1" title={travelOption.itineraryPortsOfCall}>
              <FaMapMarkerAlt className="mb-1 h-3 text-theme inline" />
              <div className="ml-1 inline">
                <PortsOfCall itineraryPortsOfCall={travelOption.itineraryPortsOfCall} />
              </div>
            </div>

            {(!travelAgency || travelAgency.isAMEXAgency) && selectedItineraryRow.promotion && (
              <div className="mt-4">
                <AdditionalInfoModal buttonLabel="Special promotion available">
                  <PromotionInfo info={selectedItineraryRow.promotion} />
                </AdditionalInfoModal>
              </div>
            )}

            {(!travelAgency || travelAgency.isAMEXAgency) &&
              selectedItineraryRow.amexGroupSailing && (
                <div className="flex flex-col items-start mt-4">
                  <span className="font-bold">
                    {selectedItineraryRow.amexGroupSailing.programType} amenities:
                  </span>
                  <AdditionalInfoModal
                    buttonLabel={selectedItineraryRow.amexGroupSailing.amenities}
                  >
                    <AMEXBenefitsInfo
                      info={{
                        title: travelOption.itineraryTitle,
                        shipName: travelOption.ship.shipName,
                        departureDate: selectedItineraryRow.itineraryDepartDate,
                        cruiseLineName: travelOption.cruiseLine.cruiseLineName,
                        ...selectedItineraryRow.amexGroupSailing,
                      }}
                    />
                  </AdditionalInfoModal>
                </div>
              )}

            {selectedItineraryRow &&
              selectedItineraryRow.virtuosoVoyage &&
              (selectedItineraryRow.virtuosoVoyage.hasPromotions ||
                selectedItineraryRow.virtuosoVoyage.hasBenefits) && (
                <div className="flex mt-4 gap-2 flex-wrap items-center">
                  {selectedItineraryRow.virtuosoVoyage.hasPromotions && (
                    <VirtuosoPromotions virtuosoVoyage={selectedItineraryRow.virtuosoVoyage} />
                  )}
                  {/*
                    Gated on hasBenefits, not isVirtuosoVoyages. Virtuoso's catalog sets
                    hasVirtuosoVoyages on sailings whose only promotion is an ordinary cruise
                    offer -- e.g. sailing 16860470 (Caribbean Princess, 13-27 Sep 2026) carries
                    it while /v2/cruise reports one promotion of promotionType "Cruise" and
                    hasVirtuosoBenefits: false. Showing the Voyages mark there claims a benefit
                    the sailing does not have. hasVirtuosoVoyages and hasVirtuosoBenefits are
                    identical across the catalog (15,320/15,320), so the list endpoint cannot
                    tell the two apart; only /v2/cruise can, via promotionType.
                  */}
                  {selectedItineraryRow.virtuosoVoyage.hasBenefits && (
                    <a
                      href={selectedItineraryRow.virtuosoVoyage.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        className="h-8 w-auto"
                        src="https://media.virtuoso.com/m/cms/VCom/vcomstaticassets/virtuoso-voyages-small.png"
                        alt="Virtuoso Voyages"
                      />
                    </a>
                  )}
                  <CruiseBenefits
                    virtuosoVoyage={selectedItineraryRow && selectedItineraryRow.virtuosoVoyage}
                  />
                </div>
              )}
          </div>
          {doShowAllRows && (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Column className="w-10" />
                  <Table.Column className="w-32">Departure</Table.Column>
                  <Table.Column className="w-32">{CabinTypeLabel[CabinType.INSIDE]}</Table.Column>
                  <Table.Column className="w-32">
                    {CabinTypeLabel[CabinType.OCEANVIEW]}
                  </Table.Column>
                  <Table.Column className="w-32">{CabinTypeLabel[CabinType.BALCONY]}</Table.Column>
                  <Table.Column className="w-32">{CabinTypeLabel[CabinType.SUITE]}</Table.Column>
                  {doShowStatusColumn && <Table.Column className="w-18" />}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {travelOption.itineraryRows.map((row) => (
                  <Table.Row
                    key={row.itineraryId}
                    isSelected={
                      selectedItineraryRow && selectedItineraryRow.itineraryId === row.itineraryId
                    }
                    onClick={() => handleItineraryRowClick && handleItineraryRowClick(row)}
                  >
                    <Table.Column>
                      <div className="inline-block">
                        <Radio
                          className="flex items-center justify-center"
                          isChecked={
                            selectedItineraryRow &&
                            selectedItineraryRow.itineraryId === row.itineraryId
                          }
                        />
                      </div>
                    </Table.Column>
                    <Table.Column>
                      {dayjs(row.itineraryDepartDate).format('MMM D, YYYY')}
                    </Table.Column>
                    <Table.Column>
                      <CostFromPrice
                        price={row.costInsideFrom.price}
                        label={CabinTypeLabel[CabinType.INSIDE]}
                      />
                    </Table.Column>
                    <Table.Column>
                      <CostFromPrice
                        price={row.costOceanviewFrom.price}
                        label={CabinTypeLabel[CabinType.OCEANVIEW]}
                      />
                    </Table.Column>
                    <Table.Column>
                      <CostFromPrice
                        price={row.costBalconyFrom.price}
                        label={CabinTypeLabel[CabinType.BALCONY]}
                      />
                    </Table.Column>
                    <Table.Column>
                      <CostFromPrice
                        price={row.costSuiteFrom.price}
                        label={CabinTypeLabel[CabinType.SUITE]}
                      />
                    </Table.Column>
                    {doShowStatusColumn && (
                      <Table.Column>
                        {doShowDebugInfo && row.itineraryState !== null && (
                          <div>
                            <span className="text-xs bg-orange-600 p-1 text-white rounded-md mr-2">
                              {row.itineraryState === EnumMergedItineraryState.ADDED
                                ? 'ADDED'
                                : 'UPDATED'}
                            </span>
                          </div>
                        )}
                        {row.itineraryStatus && (
                          <div>
                            <span className="text-xs bg-orange-600 p-1 text-white rounded-md capitalize">
                              {row.itineraryStatus}
                            </span>
                          </div>
                        )}
                      </Table.Column>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </div>
      <AgentTipsInline
        canShowAgentTips={canShowAgentTips}
        cruiseDepartDate={selectedItineraryRow.itineraryDepartDate}
        cruiseReturnDate={selectedItineraryRow.itineraryReturnDate}
        departureCityId={travelOption.itineraryDepartCityId}
        handleDoShowAgentTip={handleDoShowAgentTip}
        itineraryId={selectedItineraryRow.itineraryId}
        selectedItineraryRow={selectedItineraryRow}
      />
      {handleViewCabinsClick && (
        <div className="w-64 mx-auto mb-5">
          <Button
            size={Button.buttonSizes.MD}
            variant={Button.buttonVariants.PRIMARY}
            onClick={handleViewCabinsClick}
          >
            {!isInsideModal ? (
              <span ref={viewRulesEl}>
                View cruise option
                <FaChevronDown className="inline-block ml-3" />
              </span>
            ) : (
              <span>
                View less
                <FaChevronUp className="inline-block ml-3" />
              </span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

CruiseInformation.propTypes = {
  isInsideModal: PropTypes.bool,
  selectedItineraryRow: PropTypes.objectOf(PropTypes.objectOf),
  handleViewCabinsClick: PropTypes.func,
  handleItineraryRowClick: PropTypes.func,
  handleDoShowAgentTip: PropTypes.func,
  itineraryId: PropTypes.string,
  canShowAgentTips: PropTypes.bool,
  cruiseItinerary: PropTypes.objectOf(PropTypes.objectOf),
  cruiseOption: PropTypes.objectOf(PropTypes.objectOf),
  doHideAllRows: PropTypes.bool,
};

CruiseInformation.defaultProps = {
  isInsideModal: false,
  selectedItineraryRow: undefined,
  handleViewCabinsClick: undefined,
  handleItineraryRowClick: undefined,
  itineraryId: undefined,
  canShowAgentTips: false,
  handleDoShowAgentTip: undefined,
  cruiseItinerary: undefined,
  cruiseOption: undefined,
  doHideAllRows: false,
};

export default memo(CruiseInformation);
